<script lang="ts">
import { computed, defineComponent, PropType, ref, Ref, watch } from "vue";
import { SpectroInfo } from './geoJS/geoJSUtils';
import {
  deleteFileAnnotation,
  FileAnnotation,
  patchFileAnnotation,
  Species,
  UpdateFileAnnotation,
  submitFileAnnotation,
} from "../api/api";
import { deleteNABatFileAnnotation, patchNABatFileAnnotationLocal } from "../api/NABatApi";
import useState from "@use/useState";
import SpeciesEditor from "./SpeciesEditor.vue";
import SpeciesNABatSave from "./SpeciesNABatSave.vue";
export default defineComponent({
  name: "AnnotationEditor",
  components: {
    SpeciesEditor,
    SpeciesNABatSave,
  },
  props: {
    spectroInfo: {
      type: Object as PropType<SpectroInfo | undefined>,
      default: () => undefined,
    },
    annotation: {
      type: Object as PropType<FileAnnotation | null>,
      default: () => null,
    },
    species: {
        type: Array as PropType<Species[]>,
        required: true,
    },
    recordingId: {
        type: Number,
        required: true,
    },
    apiToken: {
      type: String,
      default: () => '',
    },
    type: {
      type: String as PropType<'nabat' | null>,
      default: () => null,
    },
    submittedAnnotationId: {
      type: Number as PropType<number | undefined>,
      default: () => undefined,
    },
  },
  emits: ['update:annotation', 'delete:annotation', 'submit:annotation'],
  setup(props, { emit }) {
    const { configuration, currentUser } = useState();
    const updatingAnnotation = ref(false);
    const deletingAnnotation = ref(false);
    const submittingAnnotation = ref(false);
    const submitConfirmOpen = ref(false);
    const speciesEdit: Ref<string[]> = ref( props.annotation?.species?.map((item) => item.species_code || item.common_name) || []);
    const comments: Ref<string> = ref(props.annotation?.comments || '');
    const confidence: Ref<number> = ref(props.annotation?.confidence || 1.0);
    const singleSpecies: Ref<string | null> = ref(props.annotation?.species.length ? props.annotation.species[0].species_code : null);
    watch(() => props.annotation, () => {
        if (props.annotation?.species) {
            speciesEdit.value = props.annotation.species.map((item) => item.species_code || item.common_name);
        }
        if (props.annotation) {
            comments.value = props.annotation.comments || '';
        }
        if (props.annotation) {
            confidence.value = props.annotation.confidence;
        }
    });
    const updateAnnotation = async () => {
        if (!props.annotation) return;
        updatingAnnotation.value = true;
        try {
            const speciesIds: number[] = [];
            speciesEdit.value.forEach((item) => {
                const found = props.species.find((specie) => specie.species_code === item);
                if (found) {
                  speciesIds.push(found.id);
                }
            });

            const updateAnnotation: UpdateFileAnnotation & { apiToken?: string } = {
              recordingId: props.recordingId,
              comments: comments.value,
              confidence: confidence.value,
              model: 'User Defined',
              species: speciesIds,
              id: props.annotation.id,
              apiToken: props.apiToken,
            };
            if (props.type === 'nabat') {
              await patchNABatFileAnnotationLocal(props.annotation.id, updateAnnotation);
            } else {
              await patchFileAnnotation(props.annotation.id, updateAnnotation);
            }
            emit('update:annotation');
        } finally {
            updatingAnnotation.value = false;
        }
    };

    const serverSpeciesCodes = () =>
      props.annotation?.species?.map((item) => item.species_code || item.common_name) ?? [];
    const speciesCodesEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    const onSpeciesModelValue = (newVal: string[]) => {
      if (speciesCodesEqual(newVal, serverSpeciesCodes())) return;
      updateAnnotation();
    };

    const onSaveComment = (newComment: string) => {
      comments.value = newComment;
      updateAnnotation();
    };

    const deleteAnnotation = async () => {
      if (props.annotation && props.recordingId) {
        deletingAnnotation.value = true;
        try {
          if (props.type === 'nabat') {
            await deleteNABatFileAnnotation(props.annotation.id, props.apiToken, props.recordingId);
          } else {
            await deleteFileAnnotation(props.annotation.id,);
          }
          emit('delete:annotation');
        } finally {
          deletingAnnotation.value = false;
        }
      }
    };

    const submitAnnotation = () => {
      submitConfirmOpen.value = true;
    };

    const closeSubmitConfirm = () => {
      submitConfirmOpen.value = false;
    };

    const confirmSubmitAnnotation = async () => {
      if (!props.annotation || !props.recordingId) return;
      submittingAnnotation.value = true;
      try {
        const response = await submitFileAnnotation(props.annotation.id);
        submitConfirmOpen.value = false;
        emit('submit:annotation', props.annotation, response.data.submitted);
      } finally {
        submittingAnnotation.value = false;
      }
    };

    const canSubmit = computed(() => (
      props.annotation
      && props.annotation.owner === currentUser.value
      && props.annotation.model === 'User Defined'
      && configuration.value.mark_annotations_completed_enabled
    ));

    const submissionTooltip = computed(() => {
      if (props.submittedAnnotationId !== undefined && props.submittedAnnotationId !== props.annotation?.id) {
        return 'You have already submitted a different annotation for this recording.';
      }
      if (props.annotation && props.annotation.submitted) {
        return 'This annotation has been submitted. This cannot be undone.';
      }
      if (!speciesEdit.value.length) {
        return 'Select at least one species before submitting.';
      }
      return 'Submit this annotation. This action cannot be undone.';
    });

    const deleteEnabled = computed(() => {
      if (props.type === 'nabat') return false;
      if (configuration.value.is_admin) return true;
      if (!configuration.value.mark_annotations_completed_enabled) return true;
      // In vetting mode, non-admins may only delete blank annotations
      return speciesEdit.value.length === 0;
    });

    return {
        updatingAnnotation,
        deletingAnnotation,
        submittingAnnotation,
        speciesEdit,
        confidence,
        comments,
        updateAnnotation,
        onSpeciesModelValue,
        onSaveComment,
        deleteAnnotation,
        submitAnnotation,
        confirmSubmitAnnotation,
        closeSubmitConfirm,
        submitConfirmOpen,
        singleSpecies,
        configuration,
        canSubmit,
        submissionTooltip,
        deleteEnabled,
    };
  },
});
</script>

<template>
  <v-card>
    <v-progress-linear
      v-if="updatingAnnotation || deletingAnnotation"
      indeterminate
      color="primary"
      class="mb-0"
    />
    <v-card-title>
      <v-row class="pa-2 align-center">
        <span
          v-if="updatingAnnotation"
          class="text-caption text-medium-emphasis mr-2"
        >
          Saving…
        </span>
        <span
          v-else-if="deletingAnnotation"
          class="text-caption text-medium-emphasis mr-2"
        >
          Deleting…
        </span>
        <span v-else>
          Choose Label
        </span>
        <v-tooltip
          v-if="type === 'nabat'"
          width="250"
          right
        >
          <template #activator="{ props }">
            <v-icon
              v-bind="props"
              size="x-small"
            >
              mdi-information
            </v-icon>
          </template>
          <span>
            Each user may only add one label per file. Once you have saved
            your selection it may not be deleted.
          </span>
        </v-tooltip>
        <v-spacer />
        <v-btn
          v-if="deleteEnabled"
          size="x-small"
          color="error"
          class="mt-1"
          :disabled="updatingAnnotation || deletingAnnotation"
          @click="deleteAnnotation()"
        >
          Delete Annotation<v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row>
        <SpeciesEditor
          :key="`species_${annotation?.id}`"
          v-model="speciesEdit"
          :species-list="species"
          :disabled="annotation?.submitted || updatingAnnotation || deletingAnnotation"
          :vetting-mode="configuration.mark_annotations_completed_enabled"
          :annotation-comment="comments"
          @update:model-value="onSpeciesModelValue"
          @delete-blank-annotation="deleteAnnotation"
          @save-comment="onSaveComment"
        />
      </v-row>
      <v-row v-if="type === 'nabat'">
        <SpeciesNABatSave
          :selected-species="speciesEdit"
          :species-list="species"
          :recording-id="recordingId"
          :annotation="annotation"
          :api-token="apiToken"
        />
      </v-row>
      <v-row
        v-if="type !== 'nabat' && !configuration.mark_annotations_completed_enabled"
      >
        <v-slider
          v-model="confidence"
          min="0"
          max="1"
          step="0.01"
          :label="`Confidence (${confidence.toFixed(2)})`"
          @end="updateAnnotation()"
        />
      </v-row>
      <v-row
        v-if="type !== 'nabat' && !configuration.mark_annotations_completed_enabled"
      >
        <v-textarea
          v-model="comments"
          label="Comments"
          @change="updateAnnotation()"
        />
      </v-row>
      <v-row v-if="canSubmit">
        <v-tooltip>
          <template #activator="{ props }">
            <div
              v-bind="props"
            >
              <v-btn
                flat
                color="primary"
                :disabled="speciesEdit.length === 0 || updatingAnnotation || deletingAnnotation || annotation?.submitted || (submittedAnnotationId !== undefined && annotation?.id !== submittedAnnotationId)"
                @click="submitAnnotation"
              >
                Submit
                <template #append>
                  <v-icon>mdi-check</v-icon>
                </template>
              </v-btn>
            </div>
          </template>
          {{ submissionTooltip }}
        </v-tooltip>
      </v-row>
    </v-card-text>
    <v-dialog
      v-model="submitConfirmOpen"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title>Submit annotation?</v-card-title>
        <v-card-text>
          Are you sure you want to submit this annotation? Once submitted, the annotation cannot be edited.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="submittingAnnotation"
            @click="closeSubmitConfirm"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="submittingAnnotation"
            @click="confirmSubmitAnnotation"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style lang="scss" scoped>

</style>
